import type { AIAnalysis, Task, TaskStatus } from '../types';
import type { ToolSelection } from '../constants/taskCreation';

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeTaskStatus(status: unknown): TaskStatus {
  return status === 'open' ? 'intake' : ((status as TaskStatus) || 'intake');
}

export function normalizeTaskTools(value: unknown): ToolSelection[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((entry) => {
    if (typeof entry === 'string') {
      const normalized = entry.trim();
      if (!normalized) return [];
      return [{
        id: `custom-${normalized.toLowerCase().replace(/\s+/g, '-')}`,
        name: normalized,
        category: 'other',
        isCustom: true,
      }];
    }

    if (!isObject(entry) || typeof entry.name !== 'string' || !entry.name.trim()) {
      return [];
    }

    return [{
      id: typeof entry.id === 'string' && entry.id.trim()
        ? entry.id
        : `custom-${entry.name.toLowerCase().replace(/\s+/g, '-')}`,
      name: entry.name,
      category: typeof entry.category === 'string' && entry.category.trim() ? entry.category : 'other',
      isCustom: entry.isCustom === true,
    }];
  });
}

export function normalizeTaskLinks(value: unknown): Array<{ title: string; url: string }> {
  if (!Array.isArray(value)) return [];

  return value.flatMap((entry) => {
    if (typeof entry === 'string') {
      const normalized = entry.trim();
      if (!normalized) return [];
      return [{ title: normalized, url: normalized }];
    }

    if (!isObject(entry)) return [];

    const title = typeof entry.title === 'string' && entry.title.trim()
      ? entry.title.trim()
      : typeof entry.url === 'string'
        ? entry.url.trim()
        : '';
    const url = typeof entry.url === 'string' ? entry.url.trim() : '';

    return title && url ? [{ title, url }] : [];
  });
}

export function normalizeTaskRow(row: Record<string, unknown>): Task {
  return {
    ...(row as unknown as Task),
    status: normalizeTaskStatus(row.status),
    tools: normalizeTaskTools(row.tools),
    links: normalizeTaskLinks(row.links),
    relatedLinks: normalizeTaskLinks(row.relatedLinks ?? row.related_links),
  };
}

export function normalizeAIAnalysisRow(row: Record<string, unknown>): AIAnalysis {
  return {
    ...(row as unknown as AIAnalysis),
    summary: typeof row.summary === 'string' ? row.summary : '',
    guideComponents: Array.isArray(row.guideComponents ?? row.guide_components)
      ? ((row.guideComponents ?? row.guide_components) as AIAnalysis['guideComponents'])
      : [],
    proposedSolutions: Array.isArray(row.proposedSolutions ?? row.proposed_solutions)
      ? ((row.proposedSolutions ?? row.proposed_solutions) as AIAnalysis['proposedSolutions'])
      : [],
    integrationSteps: Array.isArray(row.integrationSteps ?? row.integration_steps)
      ? ((row.integrationSteps ?? row.integration_steps) as AIAnalysis['integrationSteps'])
      : [],
    riskAssessment: isObject(row.riskAssessment ?? row.risk_assessment)
      ? ((row.riskAssessment ?? row.risk_assessment) as AIAnalysis['riskAssessment'])
      : { overallRisk: 'low', risks: [], mitigations: [] },
    automationOpportunities: Array.isArray(row.automationOpportunities ?? row.automation_opportunities)
      ? ((row.automationOpportunities ?? row.automation_opportunities) as AIAnalysis['automationOpportunities'])
      : [],
    aiProvider: (row.aiProvider ?? row.ai_provider ?? 'openai') as AIAnalysis['aiProvider'],
    model: typeof row.model === 'string' ? row.model : '',
    analysisVersion: Number(row.analysisVersion ?? row.analysis_version ?? 1),
    confidence: Number(row.confidence ?? 0),
    processingTime: Number(row.processingTime ?? row.processing_time ?? 0),
    tokensUsed: Number(row.tokensUsed ?? row.tokens_used ?? 0),
    cost: Number(row.cost ?? 0),
    status: (row.status as AIAnalysis['status']) || 'pending',
    error: typeof row.error === 'string' ? row.error : undefined,
    createdAt: String(row.createdAt ?? row.created_at ?? ''),
    completedAt: row.completedAt ?? row.completed_at ? String(row.completedAt ?? row.completed_at) : null,
    id: String(row.id ?? ''),
    taskId: String(row.taskId ?? row.task_id ?? ''),
  };
}
